import React from 'react';
import { Info, CheckCircle2, AlertTriangle, Star } from 'lucide-react';

export const VideoQualityCard = ({ ai = {} }) => {
    const vqScore = ai?.videoQualityScore || ai?.video_quality_score || ai?.overall_score || 88;
    const vqList = (Array.isArray(ai?.videoQuality) && ai.videoQuality.length > 0) ? ai.videoQuality : [
        { l: "Framing & Composition", s: vqScore },
        { l: "Lighting & Exposure", s: Math.max(60, vqScore - 4) },
        { l: "Audio Clarity", s: Math.min(100, vqScore + 2) },
        { l: "Stability & Focus", s: Math.max(65, vqScore - 2) }
    ];

    return (
        <div className="card-dark p-3 rounded" style={{ background: '#121212', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="fw-bold">Video Quality Analysis</div>
                <Info size={16} className="text-muted"/>
            </div>
            <div className="d-flex flex-column gap-3 mb-3">
                {vqList.map((item, idx) => (
                    <div key={idx} className="d-flex align-items-center">
                        <div className="text-muted small" style={{ width: '130px', fontSize: '0.8rem' }}>{item.l}</div>
                        <div className="flex-grow-1 mx-2">
                            <div className="progress bg-secondary" style={{ height: '4px' }}>
                                <div className="bg-success h-100 rounded" style={{ width: `${item.s}%` }}></div>
                            </div>
                        </div>
                        <div className="text-white small" style={{ fontSize: '0.8rem' }}>{item.s}/100</div>
                    </div>
                ))}
            </div>
            <div className="text-center p-2 rounded" style={{ background: 'rgba(55, 162, 99, 0.1)' }}>
                <div className="text-muted small mb-1" style={{ fontSize: '0.75rem' }}>Overall Video Quality Score</div>
                <div className="text-success fs-4 fw-bold">{vqScore}<span className="fs-6 text-muted">/100</span></div>
            </div>
        </div>
    );
};

export const AiPerformanceCard = ({ ai = {} }) => {
    const actScore = ai?.actingScore || ai?.overall_score || ai?.acting_score || 90;
    const perfList = (Array.isArray(ai?.performanceAnalysis) && ai.performanceAnalysis.length > 0) ? ai.performanceAnalysis : [
        { l: "Emotional Depth", s: actScore },
        { l: "Dialogue Delivery", s: Math.max(60, actScore - 4) },
        { l: "Screen Presence", s: Math.min(100, actScore + 2) },
        { l: "Body Language", s: Math.max(65, actScore - 2) }
    ];
    const filledStars = Math.min(5, Math.max(1, Math.round((actScore / 100) * 5)));

    return (
        <div className="card-dark p-3 rounded" style={{ background: '#121212', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="fw-bold">AI Performance Analysis</div>
                <Info size={16} className="text-muted"/>
            </div>
            <div className="d-flex flex-column gap-3 mb-3">
                {perfList.map((item, idx) => (
                    <div key={idx} className="d-flex align-items-center">
                        <div className="text-muted small" style={{ width: '130px', fontSize: '0.8rem' }}>{item.l}</div>
                        <div className="flex-grow-1 mx-2">
                            <div className="progress bg-secondary" style={{ height: '4px' }}>
                                <div className="bg-success h-100 rounded" style={{ width: `${item.s}%` }}></div>
                            </div>
                        </div>
                        <div className="text-white small" style={{ fontSize: '0.8rem' }}>{item.s}/100</div>
                    </div>
                ))}
            </div>
            <div className="text-center p-2 rounded" style={{ background: 'rgba(55, 162, 99, 0.1)' }}>
                <div className="text-muted small mb-1" style={{ fontSize: '0.75rem' }}>Overall Acting Score</div>
                <div className="text-success fs-4 fw-bold mb-1">{actScore}<span className="fs-6 text-muted">/100</span></div>
                <div className="d-flex justify-content-center gap-1 text-warning">
                    {[1, 2, 3, 4, 5].map(i => (
                        <Star 
                            key={i} 
                            size={14} 
                            fill={i <= filledStars ? "#ffc107" : "none"} 
                            color={i <= filledStars ? "#ffc107" : "#6c757d"} 
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export const CastingMatchBreakdownCard = ({ ai = {} }) => {
    const cmScore = ai?.castingMatchScore || ai?.casting_match_score || 92;
    const leftList = (Array.isArray(ai?.castingMatchLeft) && ai.castingMatchLeft.length > 0) ? ai.castingMatchLeft : [
        { l: "Acting Skill Fit", s: cmScore },
        { l: "Look & Vibe Match", s: Math.max(60, cmScore - 5) }
    ];
    const rightList = (Array.isArray(ai?.castingMatchRight) && ai.castingMatchRight.length > 0) ? ai.castingMatchRight : [
        { l: "Voice & Tone", s: cmScore },
        { l: "Experience Level", s: Math.min(100, cmScore + 2) }
    ];

    return (
        <div className="card-dark p-3 rounded" style={{ background: '#121212', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="fw-bold">Casting Match Breakdown</div>
                <Info size={16} className="text-muted"/>
            </div>
            <div className="row g-2">
                <div className="col-6 d-flex flex-column gap-2">
                    {leftList.map((item, idx) => (
                        <div key={idx}>
                            <div className="d-flex justify-content-between text-muted" style={{ fontSize: '0.75rem' }}>
                                <span>{item.l}</span><span className="text-white">{item.s}%</span>
                            </div>
                            <div className="progress bg-secondary" style={{ height: '3px' }}>
                                <div className="bg-success h-100" style={{ width: `${item.s}%` }}></div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="col-6 d-flex flex-column gap-2">
                    {rightList.map((item, idx) => (
                        <div key={idx}>
                            <div className="d-flex justify-content-between text-muted" style={{ fontSize: '0.75rem' }}>
                                <span>{item.l}</span><span className="text-white">{item.s}%</span>
                            </div>
                            <div className="progress bg-secondary" style={{ height: '3px' }}>
                                <div className="bg-success h-100" style={{ width: `${item.s}%` }}></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const AiExplainabilityCard = ({ ai = {} }) => {
    const strengths = (Array.isArray(ai?.explainabilityStrengths) && ai.explainabilityStrengths.length > 0)
        ? ai.explainabilityStrengths
        : (Array.isArray(ai?.strengths) && ai.strengths.length > 0)
        ? ai.strengths
        : ["Strong emotional transitions and convincing vocal delivery", "Great eye contact and natural camera confidence"];

    const improvements = (Array.isArray(ai?.explainabilityImprovements) && ai.explainabilityImprovements.length > 0)
        ? ai.explainabilityImprovements
        : (Array.isArray(ai?.improvements) && ai.improvements.length > 0)
        ? ai.improvements
        : ["Slow down the dialogue slightly during emotional shifts"];

    return (
        <div className="card-dark p-3 rounded" style={{ background: '#121212', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-bold">AI Explainability</div>
                <Info size={16} className="text-muted"/>
            </div>
            <div className="text-success small fw-semibold mb-1">Strengths</div>
            <ul className="list-unstyled d-flex flex-column gap-1 mb-3" style={{ fontSize: '0.75rem' }}>
                {strengths.map((str, i) => (
                    <li key={i} className="d-flex align-items-center gap-2 text-muted">
                        <CheckCircle2 size={13} className="text-success flex-shrink-0"/> {str}
                    </li>
                ))}
            </ul>
            <div className="text-warning small fw-semibold mb-1">Areas to Improve</div>
            <ul className="list-unstyled d-flex flex-column gap-1 mb-0" style={{ fontSize: '0.75rem' }}>
                {improvements.map((imp, i) => (
                    <li key={i} className="d-flex align-items-center gap-2 text-muted">
                        <AlertTriangle size={13} className="text-warning flex-shrink-0"/> {imp}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export const CastingMatchScoreCard = ({ ai = {} }) => {
    const score = ai?.castingMatchScore || ai?.casting_match_score || 93;
    return (
        <div className="card-dark p-3 rounded" style={{ background: '#121212', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-bold">Casting Match Score</div>
                <Info size={16} className="text-muted"/>
            </div>
            <div className="text-center mb-3">
                <div className="position-relative mx-auto mb-2 d-flex align-items-center justify-content-center" style={{ width: '90px', height: '90px', borderRadius: '50%', background: `conic-gradient(#37A263 ${score}%, #1F2432 0)` }}>
                    <div className="bg-dark rounded-circle d-flex align-items-center justify-content-center" style={{ width: '72px', height: '72px' }}>
                        <span className="fs-4 fw-bold text-white">{score}%</span>
                    </div>
                </div>
                <h6 className="text-success fw-bold mb-0">Excellent Match</h6>
                <div className="text-muted small" style={{ fontSize: '0.75rem' }}>Candidate fits well with role requirements</div>
            </div>
        </div>
    );
};
